import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Compass, Sprout } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { Reveal, Overline } from "../components/site/Reveal";
import Timeline from "../components/site/Timeline";
import FocalImage from "../components/FocalImage";
import api from "../lib/api";

const PILLAR_ICONS = [Users, Compass, Sprout];
const PILLAR_HUES = ["150 40% 34%", "22 60% 52%", "38 62% 52%"];
const DEFAULT_ORDER = ["hero", "pillars", "timeline", "stats", "cta"];
const IMG_FALLBACK = "https://images.pexels.com/photos/36488220/pexels-photo-36488220.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1400";

export default function About() {
  const { content, t } = useLang();
  const a = content.about || {};
  const pillars = content.pillars || [];
  const timeline = content.timeline || [];
  const stats = content.stats || [];

  const [layout, setLayout] = useState(null);
  const [imgs, setImgs] = useState({});
  useEffect(() => {
    api.get("/page-config/about")
      .then((r) => { setLayout(r.data.sections); setImgs(r.data.images || {}); })
      .catch(() => setLayout(DEFAULT_ORDER.map((id) => ({ id, enabled: true }))));
  }, []);

  const blocks = {
    hero: (
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-10 md:pt-16 pb-16">
        <Reveal><Overline>{a.hero_overline}</Overline></Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.05] mt-5 max-w-4xl text-primary" data-testid="about-title">{a.hero_title}</h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="text-lg text-foreground/70 leading-relaxed mt-6 max-w-2xl">{a.hero_body}</p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="overflow-hidden rounded-[2rem] mt-12 aspect-[16/8]">
            <FocalImage img={imgs.hero || { url: IMG_FALLBACK }} alt="GYBC" testid="about-hero-image" />
          </div>
        </Reveal>
      </section>
    ),
    pillars: (
      <section className="bg-card/60 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal><Overline>{t("Why we exist", "Pourquoi nous existons")}</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tight mt-4 text-primary">{t("Three things every woman deserves.", "Trois choses que chaque femme mérite.")}</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {pillars.map((p, i) => {
              const Icon = PILLAR_ICONS[i % PILLAR_ICONS.length];
              const hue = PILLAR_HUES[i % PILLAR_HUES.length];
              return (
                <Reveal key={i} delay={i * 0.1}>
                  <motion.div
                    whileHover={{ y: -8 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    className="group relative rounded-3xl p-8 h-full overflow-hidden border"
                    style={{ background: `linear-gradient(160deg, hsl(${hue} / 0.14), transparent 60%)`, borderColor: `hsl(${hue} / 0.28)` }}
                  >
                    <div className="absolute -top-8 -right-6 w-28 h-28 rounded-full blur-2xl opacity-0 group-hover:opacity-60 transition-opacity duration-500" style={{ background: `hsl(${hue} / 0.5)` }} />
                    <div className="flex items-center justify-between">
                      <motion.div whileHover={{ rotate: -8, scale: 1.08 }} transition={{ type: "spring", stiffness: 300 }}
                        className="w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-lg" style={{ background: `hsl(${hue})`, boxShadow: `0 10px 25px -8px hsl(${hue} / 0.6)` }}>
                        <Icon size={24} />
                      </motion.div>
                      <span className="font-display text-5xl font-semibold" style={{ color: `hsl(${hue} / 0.25)` }}>0{i + 1}</span>
                    </div>
                    <h3 className="relative font-display text-2xl font-semibold text-primary mt-5">{p.title}</h3>
                    <p className="relative text-foreground/70 mt-2 leading-relaxed">{p.body}</p>
                  </motion.div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    ),
    timeline: (
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <div className="text-center max-w-2xl mx-auto">
          <Reveal><Overline>{t("The journey", "Le parcours")}</Overline></Reveal>
          <Reveal delay={0.1}><h2 className="font-display text-4xl md:text-5xl font-medium tracking-tight mt-3 text-primary">{t("Every chapter, written together.", "Chaque chapitre, écrit ensemble.")}</h2></Reveal>
        </div>
        <Timeline items={timeline} />
      </section>
    ),
    stats: (
      <section className="bg-primary text-primary-foreground py-20 grain">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <p className="font-display text-5xl md:text-6xl font-semibold">{s.value}</p>
              <p className="text-primary-foreground/60 mt-2">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>
    ),
    cta: (
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20 text-center">
        <Reveal>
          <h2 className="font-display text-4xl md:text-5xl font-medium text-primary">{t("Come find your circle.", "Venez trouver votre cercle.")}</h2>
          <Link to="/join" className="inline-block mt-8 rounded-full bg-secondary text-secondary-foreground px-8 py-4 font-semibold hover:opacity-90 transition-opacity">
            {t("Become a member", "Devenir membre")}
          </Link>
        </Reveal>
      </section>
    ),
  };

  const order = layout || DEFAULT_ORDER.map((id) => ({ id, enabled: true }));

  return (
    <div data-testid="about-page">
      {order.filter((s) => s.enabled && blocks[s.id]).map((s) => (
        <React.Fragment key={s.id}>{blocks[s.id]}</React.Fragment>
      ))}
    </div>
  );
}
